import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 * @property {HTMLElement} headerColumns - The header columns element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerRowTop', 'headerColumns'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Tracks announcement bar height changes
   * @type {ResizeObserver | null}
   */
  #announcementResizeObserver = null;

  /**
   * Cached announcement bar element
   * @type {HTMLElement | null}
   */
  #announcementBar = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Cached header group height value
   * @type {number}
   */
  #headerGroupHeight = 0;

  /**
   * Whether the page is scrolled
   * @type {boolean}
   */
  #isScrolled = false;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
    this.#updateMobileStickyHeaderHeight();
    this.#updateAnnouncementBarHeight();

    this.#headerGroupHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Updates the CSS variable for the mobile sticky header height
   */
  #updateMobileStickyHeaderHeight = () => {
    const headerDrawerContainer = this.refs.headerDrawerContainer;
    if (!headerDrawerContainer) return;

    const { height } = headerDrawerContainer.getBoundingClientRect();
    document.body.style.setProperty('--mobile-sticky-header-height', `${height}px`);
  };

  /**
   * Updates the CSS variable representing the announcement bar height
   */
  #updateAnnouncementBarHeight = () => {
    if (!this.#announcementBar || !this.#announcementBar.isConnected) {
      this.#announcementBar = document.querySelector('.announcement-bar');
    }

    const height =
      this.#announcementBar instanceof HTMLElement ? this.#announcementBar.offsetHeight : 0;

    document.body.style.setProperty('--announcement-bar-height', `${height}px`);
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }

    this.#updateMobileStickyHeaderHeight();
  }

  /**
   * @param {boolean} isScrollingUp
   */
  #updateScrollDirection = (isScrollingUp) => {
    this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
  };

  /**
   * @param {number} scrollTop
   * @param {number} threshold
   */
  #handleAlwaysMode = (scrollTop, threshold) => {
    if (scrollTop > threshold) {
      this.dataset.stickyState = 'active';
    } else {
      this.removeAttribute('data-sticky-state');
    }
  };

  #resetStickyState = () => {
    this.removeAttribute('data-sticky-animation');
    this.removeAttribute('data-sticky-state');
    this.removeAttribute('data-scroll-direction');
    this.removeAttribute('data-was-active');
  };

  #setStickyHidden = () => {
    this.dataset.stickyAnimation = 'hidden';
    this.dataset.stickyState = 'none';
  };

  #setStickyVisible = () => {
    this.dataset.stickyAnimation = 'visible';
    this.dataset.stickyState = 'active';
    this.dataset.wasActive = 'true';
  };

  /**
   * @param {number} scrollTop
   * @param {number} threshold
   * @param {boolean} isScrollingUp
   */
  #handleScrollUpMode = (scrollTop, threshold, isScrollingUp) => {
    if (scrollTop <= this.#headerGroupHeight) {
      this.#resetStickyState();
    } else if (scrollTop > this.#headerGroupHeight && scrollTop < threshold) {
      this.#setStickyHidden();
    } else if (scrollTop >= threshold) {
      if (isScrollingUp) {
        this.#setStickyVisible();
      } else {
        this.#setStickyHidden();
      }
    }
  };

  #handleScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const threshold = this.#headerGroupHeight * 2;

    this.#handlePageScrolled(scrollTop, threshold);
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    this.#updateScrollDirection(isScrollingUp);

    if (stickyMode === 'always') {
      this.#handleAlwaysMode(scrollTop, threshold);
    } else if (stickyMode === 'scroll-up') {
      this.#handleScrollUpMode(scrollTop, threshold, isScrollingUp);
    }

    this.#lastScrollTop = scrollTop;
  };

  #handlePageScrolled = (scrollTop = 0, threshold = 0) => {
    const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
    const meetsScrollRequirement = isMobileViewport
      ? threshold > 0 && scrollTop >= threshold
      : scrollTop > 0;

    const hasScrollLock = document.documentElement.hasAttribute('scroll-lock');
    const isHeaderStickyActive = this.dataset.stickyState === 'active';
    const wasHeaderActive = this.dataset.wasActive === 'true';

    // On mobile, when scroll-lock is active, scrollTop may be 0 even if page was scrolled.
    // Check the scroll-lock-offset CSS variable to determine if page was scrolled before lock.
    let scrollLockOffset = 0;
    if (hasScrollLock) {
      const offsetValue =
        getComputedStyle(document.documentElement).getPropertyValue('--scroll-lock-offset') || '';
      if (offsetValue) {
        // Parse the offset (e.g., "-100px" -> 100)
        const match = offsetValue.match(/-?(\d+(?:\.\d+)?)px/);
        if (match && match[1]) {
          scrollLockOffset = Math.abs(parseFloat(match[1]));
        }
      }
    }

    const wasScrolledBeforeLock = scrollLockOffset > 0;
    const shouldForceScrolled =
      hasScrollLock &&
      (!isMobileViewport || isHeaderStickyActive || wasHeaderActive || wasScrolledBeforeLock);

    this.#isScrolled = meetsScrollRequirement || shouldForceScrolled;

    if (this.#isScrolled) {
      document.documentElement.setAttribute('page-scrolled', '');
    } else {
      document.documentElement.removeAttribute('page-scrolled');
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    this.#headerGroupHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;

    this.#announcementBar = document.querySelector('.announcement-bar');
    if (this.#announcementBar instanceof HTMLElement) {
      this.#announcementResizeObserver = new ResizeObserver(this.#updateAnnouncementBarHeight);
      this.#announcementResizeObserver.observe(this.#announcementBar);
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#announcementResizeObserver?.disconnect();
    document.removeEventListener('scroll', this.#handleScroll);
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--mobile-sticky-header-height', '0px');
    document.body.style.setProperty('--announcement-bar-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() =>
      calculateHeaderGroupHeight(header, headerGroup)
    );

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
